'use client'

import { AgendaList } from '@/components/dashboard/agenda-list'
import { PuntoRojo } from '@/components/dashboard/punto-rojo'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/auth-context'
import { usePacientes } from '@/hooks/use-pacientes'
import { useKPIs, useKPIsProfesionales, type KPIProfesional } from '@/hooks/use-kpis'
import Link from 'next/link'
import { CRMHeader, CRMKPIGrid, CRMEmptyState, type KPICardItem } from '@/components/shared/crm-ui'
import { CRMErrorState } from '@/components/shared/page-states'
import { cn } from '@/lib/utils'
import { TrendingUp, Users, Calendar, AlertTriangle, BarChart3 } from 'lucide-react'

// -- Formatters --

function formatARS(value: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function trendPct(current: number, previous: number): number {
  if (previous === 0) return 0
  return Math.round(((current - previous) / previous) * 100)
}

// -- EmptyState Component --

interface EmptyStateProps {
  title: string
  description: string
  icon: 'Calendar' | 'AlertTriangle' | 'BarChart3'
  action?: { label: string; onClick: () => void }
}

const ICON_MAP = {
  Calendar,
  AlertTriangle,
  BarChart3,
} as const

function EmptyState({ title, description, icon, action }: EmptyStateProps) {
  const IconComponent = ICON_MAP[icon]

  return (
    <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-border bg-card">
      <div className="flex flex-col items-center gap-3 text-center">
        <IconComponent className="w-12 h-12 text-muted-foreground" size={48} />
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground max-w-sm">{description}</p>
        {action && (
          <Button
            variant="outline"
            size="sm"
            onClick={action.onClick}
            className="mt-2"
          >
            {action.label}
          </Button>
        )}
      </div>
    </div>
  )
}

// -- Mini Bar Chart --

interface BarData {
  label: string
  value: number
}

function MiniBarChart({ data, title }: { data: BarData[]; title: string }) {
  const max = Math.max(...data.map(d => d.value), 1)
  const todoCero = data.every(d => d.value === 0)

  // 2026-09-30 (auditoría visual): con todos los valores en 0 el gráfico era
  // solo leyendas sin información — mostrar empty state como el panel vecino.
  if (todoCero) {
    return (
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
        <div className="h-20 flex flex-col items-center justify-center gap-1 text-center">
          <p className="text-[13px] font-medium text-muted-foreground">Sin actividad este mes</p>
          <p className="text-[11px] text-muted-foreground/70">Los turnos del mes se graficarán acá.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="flex items-end gap-1.5 h-20">
        {data.map((bar, i) => {
          const heightPct = Math.round((bar.value / max) * 100)
          return (
            <div
              key={i}
              className="flex flex-col items-center gap-1 flex-1 min-w-0"
              title={`${bar.label}: ${bar.value}`}
            >
              <div className="w-full flex flex-col justify-end" style={{ height: '64px' }}>
                <div
                  className="w-full rounded-t-sm bg-primary/70 transition-all duration-500"
                  style={{ height: `${heightPct}%` }}
                  role="img"
                  aria-label={`${bar.label}: ${bar.value}`}
                />
              </div>
              <span className="text-[9px] text-muted-foreground truncate w-full text-center leading-tight">
                {bar.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// -- Profesionales table --

function ProfesionalesTable({ data }: { data: KPIProfesional[] }) {
  if (data.length === 0) {
    return (
      <EmptyState
        title="Sin datos de productividad"
        description="No hay actividad registrada este mes."
        icon="BarChart3"
      />
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted border-b border-border">
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Profesional</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Turnos</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Facturación</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Ocupación</th>
          </tr>
        </thead>
        <tbody>
          {data.map(p => (
            <tr key={p.profesional_id} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
              <td className="px-4 py-2.5 font-medium text-foreground">{p.nombre}</td>
              <td className="px-4 py-2.5 text-right tabular text-foreground">{p.turnos_completados}</td>
              <td className="px-4 py-2.5 text-right tabular text-foreground hidden sm:table-cell">{formatARS(p.facturacion)}</td>
              <td className="px-4 py-2.5 text-right hidden sm:table-cell">
                <span className={cn(
                  'text-xs font-semibold',
                  p.tasa_ocupacion_pct >= 80 ? 'text-status-success' :
                  p.tasa_ocupacion_pct >= 50 ? 'text-status-warning' :
                  'text-status-error',
                )}>
                  {formatPct(p.tasa_ocupacion_pct)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// -- Page --

export default function Dashboard() {
  const { user } = useAuth()
  const { data: pacientes } = usePacientes()
  const { data: kpis, isLoading: kpisLoading, error: kpisError, refetch } = useKPIs()
  const { data: profesionales = [] } = useKPIsProfesionales()

  const hoy = new Date().toDateString()
  const pacientesHoyCount = (pacientes ?? []).filter(p => {
    if (!p.proximo_turno?.fecha_hora) return false
    return new Date(p.proximo_turno.fecha_hora).toDateString() === hoy
  }).length

  // Build KPI items
  const kpiItems: KPICardItem[] = kpisLoading
    ? Array.from({ length: 6 }, () => ({ label: '', value: '', isLoading: true }))
    : kpis
    ? [
        {
          label: 'Facturación mensual',
          value: formatARS(kpis.facturacion_mes_actual ?? 0),
          trend: { value: trendPct(kpis.facturacion_mes_actual ?? 0, kpis.facturacion_mes_anterior ?? 0), label: 'vs mes ant.' },
          icon: <TrendingUp className="size-3.5" aria-hidden />,
        },
        {
          label: 'Ocupación',
          value: formatPct(kpis.tasa_ocupacion_pct ?? 0),
          icon: <TrendingUp className="size-3.5" aria-hidden />,
        },
        {
          label: 'Ticket promedio',
          value: formatARS(kpis.ticket_promedio ?? 0),
        },
        {
          label: 'Conversión',
          value: formatPct(kpis.tasa_conversion_pct ?? 0),
        },
        {
          label: 'No-shows',
          value: formatPct(kpis.tasa_no_show_pct ?? 0),
        },
        {
          label: 'Pac. nuevos',
          value: kpis.pacientes_nuevos ?? 0,
          trend: { value: trendPct(kpis.pacientes_nuevos ?? 0, kpis.pacientes_nuevos_mes_anterior ?? 0), label: 'vs mes ant.' },
          icon: <Users className="size-3.5" aria-hidden />,
        },
      ]
    : []

  // Bar chart data for turnos
  const turnosBarData: { label: string; value: number }[] = kpis
    ? [
        { label: 'Completados', value: kpis.turnos_completados ?? 0 },
        { label: 'Cancelados',  value: kpis.turnos_cancelados ?? 0  },
        { label: 'No-show',     value: Math.round(((kpis.tasa_no_show_pct ?? 0) / 100) * (kpis.turnos_completados ?? 0)) },
      ]
    : []

  return (
    <div className="space-y-8 animate-fade-in text-foreground">
      <CRMHeader
        title={`Hola, ${user?.nombre ?? 'Doctor'}`}
        subtitle={
          pacientesHoyCount > 0
            ? `${pacientesHoyCount} ${pacientesHoyCount === 1 ? 'paciente hoy' : 'pacientes hoy'}`
            : 'Sin turnos hoy'
        }
      >
      </CRMHeader>

      {/* KPI Grid */}
      {kpisError ? (
        <CRMErrorState
          title="No se pudieron cargar los KPIs"
          description="Verificá la conexión e intentá de nuevo."
          onRetry={() => refetch()}
        />
      ) : (
        <CRMKPIGrid items={kpiItems} />
      )}

      {/* Turnos bar chart + Profesionales table */}
      {!kpisLoading && kpis && (
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-border bg-card px-5 py-4">
            <MiniBarChart data={turnosBarData} title="Turnos del mes" />
          </div>
          <div className="rounded-xl border border-border bg-card px-5 py-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Por profesional
            </p>
            <ProfesionalesTable data={profesionales} />
          </div>
        </div>
      )}

      {/* Agenda + Alertas */}
      <div className="grid gap-8 md:grid-cols-3 items-start mt-4">
        {/* Agenda - Próximos turnos */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-[14px] font-semibold tracking-tight text-foreground/90 uppercase tracking-wider">
              Próximos turnos
            </h2>
            <Link href="/agenda">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-[12px] text-muted-foreground font-medium hover:text-primary transition-colors"
              >
                Ver todo
              </Button>
            </Link>
          </div>
          <AgendaList />
        </div>

        {/* Pacientes de hoy - Punto Rojo */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-[14px] font-semibold tracking-tight text-foreground/90 uppercase tracking-wider">
              Hoy
            </h2>
            {pacientesHoyCount > 0 && (
              <span className="text-[11px] text-muted-foreground tabular font-semibold">{pacientesHoyCount}</span>
            )}
          </div>
          <EmptyState
            title="Sin alertas hoy"
            description="No hay alertas de punto rojo para hoy."
            icon="AlertTriangle"
          />
        </div>
      </div>
    </div>
  )
}